import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "../api";
import { PageHead, Filters, Kpi, Panel, LoadingBlock } from "../shell";
import { money, pct, growthClass, achievementSeverity } from "../lib/format";
import { ScopeViewSwitch } from "../components/ScopeViewSwitch";

function useRegionScopeView(user) {
  const canViewCompany = user?.regions_company_view === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewScope, setViewScope] = useState(canViewCompany && searchParams.get("view_scope") === "company" ? "company" : "self");
  const selectViewScope = (next) => {
    const allowed = next === "self" || canViewCompany;
    if (!allowed) return;
    setViewScope(next);
    const params = new URLSearchParams(searchParams);
    if (next === "company") params.set("view_scope", "company");
    else params.delete("view_scope");
    setSearchParams(params, { replace: true });
  };
  useEffect(() => {
    if (!canViewCompany && viewScope === "company") {
      setViewScope("self");
      const params = new URLSearchParams(searchParams);
      params.delete("view_scope");
      setSearchParams(params, { replace: true });
    }
  }, [canViewCompany, viewScope, searchParams, setSearchParams]);
  return { viewScope, selectViewScope, canViewCompany };
}

export function Regions({ user, dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { viewScope, selectViewScope, canViewCompany } = useRegionScopeView(user);
  // Primitive period fields intentionally trigger reload; periodParams is derived upstream.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setData(null);
    api.get("/analytics/regions", { params: { ...periodParams, view_scope: viewScope } })
      .then((r) => setData(r.data))
      .catch((error) => { if (error.response?.status === 403 && viewScope === "company") selectViewScope("self"); });
  }, [period, year, startDate, endDate, viewScope]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <main className="content">
      <PageHead eyebrow="ORGANİZASYON" title="Bölgeler" desc="Bölgesel üretim ve hedef performansını karşılaştırın." />
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={dataset?.state?.active === "DEMO"}
               actions={<ScopeViewSwitch value={viewScope} onChange={selectViewScope} canViewCompany={canViewCompany} testidPrefix="regions" />} />
      {!data ? <LoadingBlock/> : (
        <>
          <div className="period-caption" data-testid="regions-period-label">{data.period}</div>
          <section className="region-grid">
            {data.items.map((r) => (
              <button data-testid={`region-card-${r.id}`} key={r.id} className="region-card" onClick={() => navigate(`/regions/${r.id}${viewScope === "company" ? "?view_scope=company" : ""}`)}>
                <div className="rc-head">
                  <strong>{r.name}</strong>
                  <span className={"pill " + achievementSeverity(r.achievement)}>{pct(r.achievement)}</span>
                </div>
                <div className="rc-metrics">
                  <div><span>Net üretim</span><b>{money(r.premium, true)}</b></div>
                  <div><span>Komisyon</span><b>{money(r.commission, true)}</b></div>
                  <div><span>Hedef</span><b>{money(r.target, true)}</b></div>
                  <div><span>GY komisyon</span><b>{money(r.previous, true)}</b></div>
                  <div><span>Satıcı</span><b>{r.seller_count}</b></div>
                  <div><span>Poliçe</span><b>{new Intl.NumberFormat("tr-TR").format(r.policy_count)}</b></div>
                </div>
                <div className="rc-footer">
                  <span className={growthClass(r.growth)}>{r.growth !== null ? `Büyüme ${pct(r.growth)}` : "GY verisi yok"}</span>
                  <ArrowUpRight size={16}/>
                </div>
              </button>
            ))}
          </section>
        </>
      )}
    </main>
  );
}

export function RegionDetail({ user, dataset, period, setPeriod, year, setYear, startDate, setStartDate, endDate, setEndDate, periodParams }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { viewScope, selectViewScope, canViewCompany } = useRegionScopeView(user);
  // Primitive period fields intentionally trigger reload; periodParams is derived upstream.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setData(null);
    api.get(`/analytics/regions/${id}`, { params: { ...periodParams, view_scope: viewScope } })
      .then((r) => setData(r.data))
      .catch((error) => { if (error.response?.status === 403 && viewScope === "company") selectViewScope("self"); });
  }, [id, period, year, startDate, endDate, viewScope]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <main className="content"><LoadingBlock/></main>;
  const k = data.kpis;
  return (
    <main className="content">
      <button data-testid="region-detail-back-button" className="back-link" onClick={() => navigate(`/regions${viewScope === "company" ? "?view_scope=company" : ""}`)}><ArrowLeft size={14}/> Tüm bölgeler</button>
      <PageHead eyebrow="BÖLGE DETAYI" title={data.region.name} desc="Bölge içindeki ekip ve satıcı performansı." />
      <Filters period={period} setPeriod={setPeriod} year={year} setYear={setYear}
               startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
               isDemo={dataset?.state?.active === "DEMO"}
               actions={<ScopeViewSwitch value={viewScope} onChange={selectViewScope} canViewCompany={canViewCompany} testidPrefix="region-detail" />} />
      <div className="period-caption" data-testid="region-detail-period-label">{data.period}</div>
      <section className="kpi-grid">
        <Kpi label="Net üretim" value={money(k.premium, true)} sub={`${k.policy_count} poliçe`}/>
        <Kpi label="Komisyon" value={money(k.commission, true)}/>
        <Kpi label="Dönem hedefi" value={money(k.target, true)}
          change={pct(k.achievement)} changeType={k.achievement >= 100 ? "positive" : k.achievement >= 90 ? "neutral" : "negative"}/>
        <Kpi label="GY komisyon" value={money(k.previous, true)}
          change={k.growth !== null ? pct(k.growth) : "—"} changeType={growthClass(k.growth)}/>
      </section>
      <section className="lower-grid">
        <Panel title="Ekipler" sub="Ekip bazında hedef gerçekleşmesi">
          <table data-testid="region-teams-table">
            <thead><tr><th>Ekip</th><th style={{textAlign:"right"}}>Prim</th><th style={{textAlign:"right"}}>Komisyon</th><th style={{textAlign:"right"}}>Hedef</th><th>Gerçekleşme</th></tr></thead>
            <tbody>{data.teams.map((t) => (
              <tr key={t.id}><td><strong>{t.name}</strong></td>
                <td style={{textAlign:"right"}}>{money(t.premium, true)}</td>
                <td style={{textAlign:"right"}}>{money(t.commission, true)}</td>
                <td style={{textAlign:"right"}}>{money(t.target, true)}</td>
                <td><span className={"pill " + achievementSeverity(t.achievement)}>{pct(t.achievement)}</span></td></tr>
            ))}</tbody>
          </table>
        </Panel>
        <Panel title="Satıcılar" sub="Satıcı bazında hedef gerçekleşmesi">
          <table data-testid="region-sellers-table">
            <thead><tr><th>Satıcı</th><th>Ekip</th><th style={{textAlign:"right"}}>Prim</th><th style={{textAlign:"right"}}>Komisyon</th><th>Gerçekleşme</th></tr></thead>
            <tbody>{data.sellers.map((s) => (
              <tr key={s.id} data-testid={`region-seller-row-${s.id}`} className={viewScope === "self" ? "clickable" : ""}
                onClick={viewScope === "self" ? () => navigate(`/sellers/${s.id}`) : undefined}>
                <td><strong>{s.name}</strong></td><td>{s.team}</td>
                <td style={{textAlign:"right"}}>{money(s.premium, true)}</td>
                <td style={{textAlign:"right"}}>{money(s.commission, true)}</td>
                <td><span className={"pill " + achievementSeverity(s.achievement)}>{pct(s.achievement)}</span></td></tr>
            ))}</tbody>
          </table>
        </Panel>
      </section>
    </main>
  );
}
